import { useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom-v5-compat";
import {
  SupportHubDestination,
  SupportHubSource,
} from "@b2bportal/core-self-serve";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useSupportChatButton } from "@tenant/self-serve";

import {
  ContactSupportModal,
  type ContactSupportModalHandle,
} from "../ContactSupportModal";
import { useTrackSupportHubHelpLaunched } from "../ContactSupportModal/supportHubTracking";
import { shouldShowChatFab } from "./chatFabRoutes";
import styles from "./HelpButton.module.scss";
import VoiceIcon from "@/assets/icons/ui/Support/Landing Page/voice.svg?react";
import {
  pathnameToEntryPointWithFallback,
  shouldOpenHtsAssistLobby,
} from "@/components/HtsAssistChatWidget/htsAssistEntryPoint";
import { pathnameToSupportContext } from "@/components/HtsAssistChatWidget/htsAssistSupportContext";
import {
  buildHtsAssistPrewarmRequest,
  useHtsAssistPrewarm,
} from "@/components/HtsAssistChatWidget/useHtsAssistPrewarm";
import { useIsHtsAssistEnabled } from "@/components/HtsAssistChatWidget/useIsHtsAssistEnabled";
import {
  openHtsAssistChat,
  openHtsAssistLobby,
} from "@/store/htsAssistEntryState";
import { useGetCurrentFunnel } from "@/utilities/hooks/useGetCurrentFunnel";
import { useMaintenanceMode } from "@/utilities/hooks/useMaintenanceMode";

export const HelpButton = () => {
  const location = useLocation();
  const modalRef = useRef<ContactSupportModalHandle>(null);
  const { t } = useI18nContext();
  const [, cn] = useModuleBEM(styles, "HelpButton");
  const { context: chatContext, handlers } = useSupportChatButton();
  const dispatch = useDispatch();
  const currentFunnel = useGetCurrentFunnel();
  const { isFunnelUnderMaintenance } = useMaintenanceMode();
  const isHtsAssistEnabled = useIsHtsAssistEnabled();
  const trackHelpLaunched = useTrackSupportHubHelpLaunched();

  const showButton =
    shouldShowChatFab(location.pathname) &&
    !isFunnelUnderMaintenance(currentFunnel);

  const prewarmRequest = useMemo(
    () =>
      buildHtsAssistPrewarmRequest({
        isEnabled: isHtsAssistEnabled,
        isLauncherVisible: showButton,
        pathname: location.pathname,
      }),
    [isHtsAssistEnabled, location.pathname, showButton]
  );
  useHtsAssistPrewarm(prewarmRequest);

  // HTS Assist uses the embedded lobby on home/search pages. The Trips list
  // owns the page lobby, so its launcher and all trip-detail launchers open
  // regular Assist directly. Flag off opens the legacy menu.
  const handleClick = useCallback(() => {
    if (isHtsAssistEnabled) {
      const entryPoint = pathnameToEntryPointWithFallback(location.pathname);
      const opensLobby = shouldOpenHtsAssistLobby(location.pathname);

      if (!chatContext.hasActiveChatSession) {
        if (opensLobby) {
          dispatch(openHtsAssistLobby({ entryPoint: entryPoint ?? "home" }));
        } else {
          dispatch(
            openHtsAssistChat({
              entryPoint,
              supportContext: pathnameToSupportContext(location.pathname),
            })
          );
        }

        trackHelpLaunched(SupportHubDestination.hts_assist);
      }
      handlers.toggleChat();
    } else {
      trackHelpLaunched(SupportHubDestination.support_hub);
      modalRef.current?.open();
    }
  }, [
    chatContext.hasActiveChatSession,
    dispatch,
    isHtsAssistEnabled,
    handlers,
    location.pathname,
    modalRef,
    trackHelpLaunched,
  ]);

  if (!showButton) {
    return null;
  }

  return (
    <>
      <ContactSupportModal
        ref={modalRef}
        source={SupportHubSource.help_launcher}
        trigger={false}
      />
      <div className={cn("container")}>
        <button
          type="button"
          className={cn("button")}
          onClick={handleClick}
          aria-label={t("chat.fabTitle")}
        >
          <VoiceIcon className={cn("icon")} />
          <span className={cn("title")}>{t("chat.fabTitle")}</span>
        </button>
      </div>
    </>
  );
};
